/**
 * UnsavedChangesDialog
 *
 * A confirmation dialog shown when attempting to close a drawer
 * that has unsaved changes.
 *
 * Features:
 * - Appears above all drawers (z-index 350)
 * - Cancel returns to drawer
 * - Discard closes drawer and loses changes
 * - Escape key cancels (doesn't discard)
 * - Focus trapped in dialog
 *
 * @see docs/stories/DRAWER-1.5-handle-unsaved-changes-protection.md
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface UnsavedChangesDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Callback when user clicks Cancel (return to drawer) */
  onCancel: () => void;
  /** Callback when user clicks Discard (close drawer, lose changes) */
  onDiscard: () => void;
  /** Optional title of the drawer with unsaved changes */
  drawerTitle?: string;
  /**
   * Optional third action: keep the changes.
   *
   * ⚠️ OPTIONAL SO THE TEN EXISTING CALLERS ARE UNCHANGED. Without it the
   * dialog is exactly what it was, Cancel and Discard. With it the user gets
   * the choice they actually want when they have just done real work — a
   * dialog offering only "lose it" is a dialog people resent.
   */
  onSave?: () => void;
  /** Label for the save action. Defaults to "Save changes". */
  saveLabel?: string;
}

/**
 * Dialog component for confirming discard of unsaved changes.
 *
 * @example
 * ```tsx
 * <UnsavedChangesDialog
 *   isOpen={showConfirmDialog}
 *   onCancel={() => setShowConfirmDialog(false)}
 *   onDiscard={() => {
 *     closeDrawer({ force: true });
 *     setShowConfirmDialog(false);
 *   }}
 *   drawerTitle="Create Task"
 * />
 * ```
 */
export function UnsavedChangesDialog({
  isOpen,
  onCancel,
  onDiscard,
  drawerTitle,
  onSave,
  saveLabel = "Save changes",
}: UnsavedChangesDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent
        className="z-[350]"
        onEscapeKeyDown={(e) => {
          // Escape cancels (returns to drawer), doesn't discard
          e.preventDefault();
          onCancel();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            {onSave
              ? drawerTitle
                ? `You have unsaved changes in "${drawerTitle}". Save them, or discard them?`
                : "You have unsaved changes. Save them, or discard them?"
              : drawerTitle
                ? `You have unsaved changes in "${drawerTitle}". Discard them?`
                : "You have unsaved changes. Discard them?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* ⚠️ The existing wording is kept for the ten callers that do not
              offer a save. "Keep editing" is clearer than "Cancel" in a dialog
              about cancelling, but changing copy on ten surfaces is not this
              change's business. */}
          <AlertDialogCancel onClick={onCancel}>
            {onSave ? "Keep editing" : "Cancel"}
          </AlertDialogCancel>
          {/* ⚠️ Discard is NOT the primary action when saving is on offer.
              The destructive styling stays, but Save sits last, where the
              confirming button belongs, so the reflexive click keeps the work
              rather than destroying it. */}
          <AlertDialogAction
            onClick={onDiscard}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {onSave ? "Discard changes" : "Discard Changes"}
          </AlertDialogAction>
          {onSave && <AlertDialogAction onClick={onSave}>{saveLabel}</AlertDialogAction>}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
